import React, { useId, useState } from "react";
import { Box } from "@mui/material";
import { Wordmark } from "@trusty/shared";
import { GRADIENT_BACKGROUND } from "../../styles";
import { BRAND, FONT_HEADING, FONT_BODY } from "./havenBrand";
import { COARSE_POINTER } from "../../lib/a11y";

// The signed-out look (AuthScreen) shared with the signup steps that follow it (ENG-3454).

export const TEXT_MUTED = "rgba(4, 43, 28, 0.7)";
export const BORDER = "rgba(4, 43, 28, 0.16)";
/** Field error text. The error border's red is about 4.1:1 on white: enough for a border, not for text. */
export const ERROR_TEXT = "#8a1c1c";

const TEXT_LINK_FOCUS_SX = {
  borderRadius: "4px",
  "&:focus-visible": { outline: `2px solid ${BRAND.elm}`, outlineOffset: "2px" },
  // ENG-3356: inline-flex, so the touch minimum applies to a span.
  [COARSE_POINTER]: { display: "inline-flex", alignItems: "center", justifyContent: "center", minWidth: 40, minHeight: 40 },
};

export const TextLink: React.FC<{ onClick: () => void; children: React.ReactNode }> = ({ onClick, children }) => {
  const [hover, setHover] = useState(false);
  return (
    <Box component="span" role="button" tabIndex={0} onClick={onClick}
      onKeyDown={(e: React.KeyboardEvent) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      sx={TEXT_LINK_FOCUS_SX}
      style={{ fontFamily: FONT_BODY, fontWeight: 600, color: BRAND.elm, cursor: "pointer", textDecoration: hover ? "underline" : "none" }}>
      {children}
    </Box>
  );
};

export const PrimaryButton: React.FC<{ onClick?: () => void; type?: "button" | "submit"; disabled?: boolean; children: React.ReactNode }> = ({ onClick, type = "button", disabled, children }) => {
  const [hover, setHover] = useState(false);
  return (
    <button type={type} onClick={onClick} disabled={disabled}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ width: "100%", padding: "14px 18px", fontFamily: FONT_BODY, fontWeight: 600, fontSize: 16, color: BRAND.mint, background: hover ? "#063a26" : BRAND.forest, border: "none", borderRadius: 100, cursor: disabled ? "default" : "pointer", boxShadow: hover ? "0 8px 20px rgba(4,43,28,0.22)" : "0 4px 12px rgba(4,43,28,0.14)", transition: "all 0.18s ease", opacity: disabled ? 0.7 : 1 }}>
      {children}
    </button>
  );
};

/**
 * ENG-3354: the label was not tied to its input (no `htmlFor`, no `id`), so the password field had no
 * accessible name at all, and an error was a red border and nothing else: no text, no
 * `aria-invalid`. Now the message is shown under the field and is its description.
 *
 * `name` is also what the submit handlers use to move focus to the first field in error, so the
 * message is read out with the field rather than left below the button.
 */
export const Field: React.FC<{
  label: string; type: string; value: string; placeholder?: string; autoFocus?: boolean;
  name: string; autoComplete: string;
  /** What is wrong, when something is. */
  error?: string; onChange: (v: string) => void;
}> = ({ label, type, value, placeholder, autoFocus, name, autoComplete, error, onChange }) => {
  const [focus, setFocus] = useState(false);
  const id = useId();
  const errorId = `${id}-error`;
  return (
    <div style={{ marginBottom: 14 }}>
      <label htmlFor={id} style={{ display: "block", fontFamily: FONT_BODY, fontWeight: 600, fontSize: 13, color: BRAND.forest, marginBottom: 8 }}>{label}</label>
      <input
        id={id} name={name} autoComplete={autoComplete}
        type={type} value={value} placeholder={placeholder} autoFocus={autoFocus}
        aria-invalid={error ? true : undefined} aria-describedby={error ? errorId : undefined}
        onChange={(e) => onChange(e.target.value)} onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
        style={{ width: "100%", boxSizing: "border-box", padding: "13px 16px", fontFamily: FONT_BODY, fontSize: 16, color: BRAND.forest, background: BRAND.sage, border: `1.5px solid ${error ? "#d05656" : focus ? BRAND.elm : BORDER}`, borderRadius: 12, outline: "none", boxShadow: focus ? "0 0 0 3px rgba(26,122,130,0.15)" : "none", transition: "border-color 0.18s ease, box-shadow 0.18s ease" }}
      />
      {error && (
        <div id={errorId} style={{ fontFamily: FONT_BODY, fontSize: 13, lineHeight: 1.4, color: ERROR_TEXT, marginTop: 6 }}>{error}</div>
      )}
    </div>
  );
};

/** Focus the named field of a form: the first one in error, after a refused submit. */
export const focusField = (form: HTMLFormElement, name: string) => {
  const field = form.elements.namedItem(name);
  if (field instanceof HTMLElement) field.focus();
};

export const Heading: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
  <>
    <h1 style={{ fontFamily: FONT_HEADING, fontWeight: 600, fontSize: 26, lineHeight: 1.2, color: BRAND.forest, textAlign: "center", margin: "0 0 12px" }}>{title}</h1>
    {subtitle && <p style={{ fontFamily: FONT_BODY, fontSize: 15, lineHeight: 1.5, color: TEXT_MUTED, textAlign: "center", margin: "0 0 28px" }}>{subtitle}</p>}
  </>
);

/** A live region: what a step just did, announced without moving focus. */
export const Status: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <p role="status" style={{ fontFamily: FONT_BODY, fontSize: 14, lineHeight: 1.5, color: TEXT_MUTED, textAlign: "center", margin: "16px 0 0" }}>
    {children}
  </p>
);

export const Footer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p style={{ fontFamily: FONT_BODY, fontSize: 14, color: TEXT_MUTED, textAlign: "center", margin: "22px 0 0" }}>{children}</p>
);

/** The gradient page with the wordmark over one white card. */
export const OnboardingShell: React.FC<{ children: React.ReactNode; maxWidth?: number }> = ({ children, maxWidth = 400 }) => (
  <div style={{ minHeight: "100vh", background: GRADIENT_BACKGROUND, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, boxSizing: "border-box", fontFamily: FONT_BODY }}>
    <div style={{ width: "100%", maxWidth }}>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 28 }}>
        <div style={{ width: 170 }}><Wordmark /></div>
      </div>
      <div style={{ background: BRAND.white, borderRadius: 20, padding: "36px 32px", boxShadow: "0 18px 48px rgba(4,43,28,0.12), 0 4px 12px rgba(4,43,28,0.06)" }}>
        {children}
      </div>
    </div>
  </div>
);
